// Global error reporter — captures uncaught browser errors, unhandled promise
// rejections, and 5xx responses from the API, then POSTs them to /api/errors/frontend.
//
// Lives outside React on purpose so it catches errors during render too.
// Uses fetch directly (not the axios client) to avoid loops if axios itself
// throws while reporting an error.

const REPORT_ENDPOINT = "/api/errors/frontend";

// Throttle identical error reports — don't spam the backend if a render loop
// fires the same error every frame.
const RECENT_KEYS = new Map(); // key -> timestamp
const THROTTLE_MS = 60_000;     // same error reported at most once per minute

const recentlySent = (key) => {
  const now = Date.now();
  // Garbage collect old entries
  for (const [k, t] of RECENT_KEYS) {
    if (now - t > THROTTLE_MS) RECENT_KEYS.delete(k);
  }
  if (RECENT_KEYS.has(key)) return true;
  RECENT_KEYS.set(key, now);
  return false;
};

const buildKey = ({ message, stack, url }) => {
  const stackHead = (stack || "").split("\n").slice(0, 3).join("|");
  return `${url || ""}::${(message || "").slice(0, 120)}::${stackHead.slice(0, 200)}`;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const sendReport = async (payload) => {
  try {
    const token = localStorage.getItem("token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    await fetch(`${BACKEND_URL}/api${REPORT_ENDPOINT.replace(/^\/api/, "")}`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (e) {
    // Swallow — we never want the reporter itself to throw.
    // eslint-disable-next-line no-console
    console.warn("[errorReporter] send failed:", e);
  }
};

export const reportError = (input) => {
  try {
    const payload = {
      message: input?.message || "Unknown error",
      stack: input?.stack || null,
      url: input?.url || (typeof window !== "undefined" ? window.location.pathname + window.location.search : null),
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      severity: input?.severity || "error",
      component_stack: input?.component_stack || null,
      extra: input?.extra || null,
    };
    const key = buildKey(payload);
    if (recentlySent(key)) return;
    sendReport(payload);
  } catch (_) {
    // never throw
  }
};

export const installGlobalErrorReporter = () => {
  if (typeof window === "undefined") return;
  if (window.__so_error_reporter_installed) return;
  window.__so_error_reporter_installed = true;

  window.addEventListener("error", (event) => {
    // Filter out cross-origin "Script error." with no useful details — they're
    // mostly browser extensions / ad-blocker noise.
    if (!event.message || event.message === "Script error.") return;
    const errObj = event.error;
    reportError({
      message: errObj?.message || event.message,
      stack: errObj?.stack || `${event.message}\n  at ${event.filename || "?"}:${event.lineno || "?"}:${event.colno || "?"}`,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    let message = "Unhandled promise rejection";
    let stack = null;
    if (reason instanceof Error) {
      message = reason.message || message;
      stack = reason.stack || null;
    } else if (typeof reason === "string") {
      message = reason;
    } else if (reason && typeof reason === "object") {
      message = reason.message || JSON.stringify(reason).slice(0, 300);
      stack = reason.stack || null;
    }
    reportError({ message, stack, severity: "error" });
  });
};
